import React, { useState, useEffect, useRef } from 'react';

const QuestionMarkIcon = ({ onOpenModal, onCloseModal, isModalOpen, className = '' }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const containerRef = useRef(null);
    const popupRef = useRef(null);
    const hideTimeoutRef = useRef(null);
    const showTimeoutRef = useRef(null);
    const isModalOpenRef = useRef(isModalOpen);

    // Keep ref in sync with prop
    useEffect(() => {
        isModalOpenRef.current = isModalOpen;
    }, [isModalOpen]);

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
            e.nativeEvent.stopImmediatePropagation();
        }
        
        // Use ref to get the most current modal state
        const modalIsOpen = isModalOpenRef.current;
        
        if (modalIsOpen) {
            // Modal is open, close it
            if (onCloseModal) {
                onCloseModal();
            }
            // Immediately hide popup
            setIsHovered(false);
            setShowPopup(false);
        } else {
            // Modal is closed, open it
            if (onOpenModal) {
                onOpenModal();
            }
            setIsHovered(true);
            setShowPopup(true);
        }
    };

    // Keep popup visible when modal is open, hide when closed
    useEffect(() => {
        if (isModalOpen) {
            setShowPopup(true);
            setIsHovered(true);
        } else {
            // Hide popup when modal closes
            setIsHovered(false);
            setShowPopup(false);
        }
    }, [isModalOpen]);

    // Handle hover on parent element with delay
    useEffect(() => {
        const parent = containerRef.current?.closest('.group, .relative');
        if (!parent) return;

        const handleMouseEnter = () => {
            // Clear any hide timeout
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            
            // Add delay before showing (500ms)
            showTimeoutRef.current = setTimeout(() => {
                setShowPopup(true);
                setIsHovered(true);
            }, 500);
        };
        
        const handleMouseLeave = (e) => {
            // Clear show timeout if mouse leaves before delay
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
                showTimeoutRef.current = null;
            }
            
            // Check if we're moving to the popup
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && (
                popupRef.current?.contains(relatedTarget) ||
                relatedTarget === popupRef.current
            )) {
                return;
            }
            
            // Don't hide if modal is open
            if (isModalOpen) {
                return;
            }
            
            // Add a small delay before hiding to allow mouse movement to popup
            setIsHovered(false);
            hideTimeoutRef.current = setTimeout(() => {
                if (!isModalOpen) {
                    setShowPopup(false);
                }
            }, 100);
        };

        parent.addEventListener('mouseenter', handleMouseEnter);
        parent.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            parent.removeEventListener('mouseenter', handleMouseEnter);
            parent.removeEventListener('mouseleave', handleMouseLeave);
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, [isModalOpen]);

    // Keep popup visible when hovering over it
    useEffect(() => {
        const popup = popupRef.current;
        if (!popup) return;

        const handlePopupEnter = () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            setIsHovered(true);
            setShowPopup(true);
        };

        const handlePopupLeave = () => {
            // Don't hide if modal is open
            if (isModalOpen) {
                return;
            }
            
            setIsHovered(false);
            hideTimeoutRef.current = setTimeout(() => {
                if (!isModalOpen) {
                    setShowPopup(false);
                }
            }, 100);
        };

        popup.addEventListener('mouseenter', handlePopupEnter);
        popup.addEventListener('mouseleave', handlePopupLeave);

        return () => {
            popup.removeEventListener('mouseenter', handlePopupEnter);
            popup.removeEventListener('mouseleave', handlePopupLeave);
        };
    }, [isModalOpen]);

    return (
        <>
            <div
                ref={containerRef}
                className={`absolute top-0 right-0 ${className}`}
                style={{ 
                    transform: 'translate(50%, -50%)',
                    zIndex: 10
                }}
            >
                <button
                    type="button"
                    onClick={handleClick}
                    className="w-4 h-4 flex items-center justify-center text-xs font-mono opacity-0 pointer-events-none"
                    aria-label="Learn more about this feature"
                >
                    ?
                </button>
            </div>
            
            {/* Popup box that appears on hover - positioned above center of parent button */}
            {showPopup && (
                <div 
                    ref={popupRef}
                    className="absolute left-1/2"
                    style={{
                        transform: 'translateX(-50%)',
                        bottom: '100%',
                        marginBottom: '8px',
                        zIndex: isModalOpen ? 40 : 50,
                        pointerEvents: 'auto',
                        position: 'absolute'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent?.stopImmediatePropagation();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.nativeEvent?.stopImmediatePropagation();
                    }}
                >
                    {/* Black box with white question mark */}
                    <button
                        type="button"
                        onClick={handleClick}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent?.stopImmediatePropagation();
                        }}
                        className="hairline px-2 py-1 flex items-center justify-center min-w-[24px] cursor-pointer bg-neutral-900"
                        style={{ 
                            pointerEvents: 'auto',
                            zIndex: isModalOpen ? 41 : 51
                        }}
                    >
                        <span 
                            className="text-xs font-mono" 
                            style={{ color: '#FFFFFF' }}
                        >
                            ?
                        </span>
                    </button>
                    
                    {/* Carrot (arrow) pointing down - black with white border */}
                    <div className="relative">
                        {/* Outer triangle (border) - 5px for 1px visible border effect */}
                        <div 
                            className="absolute top-full left-1/2"
                            style={{
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: '5px solid #FFFFFF'
                            }}
                        />
                        {/* Inner triangle (fill) - 4px to create 1px border */}
                        <div 
                            className="absolute top-full left-1/2"
                            style={{
                                transform: 'translateX(-50%) translateY(-1px)',
                                width: 0,
                                height: 0,
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '4px solid #0D0D0D'
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default QuestionMarkIcon;

